package fyi.moko.hotpl.ui.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.produceState
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.graphics.ImageBitmap
import fyi.moko.hotpl.data.image.SharedPhotoMemoryCache
import fyi.moko.hotpl.domain.service.PhotoBlobStore
import fyi.moko.hotpl.ui.placeform.decodeImageBitmap
import kotlinx.coroutines.CancellationException
import org.koin.compose.koinInject

/**
 * A friend's shared photo (backlog 0-36): `shared/{ownerUid}/{fileName}` on the blob store,
 * through a process-lifetime memory LRU ([SharedPhotoMemoryCache]). No disk cache — the
 * social surface is online only, like the projection it came with, and a friend's bytes
 * must never land in my own photo store where they would look like mine.
 *
 * Same [StoredPhotoHandle] as my own photos so [StoredPhotoTile] draws it unchanged; the
 * states collapse to Fetching → Loaded/Unavailable (nothing is ever "local" or "lost" here).
 */
@Composable
fun rememberSharedPhoto(ownerUid: String, fileName: String?): StoredPhotoHandle {
    val blobs = koinInject<PhotoBlobStore>()
    val cache = koinInject<SharedPhotoMemoryCache>()
    var attempt by remember(ownerUid, fileName) { mutableIntStateOf(0) }
    val photo by produceState<PhotoLoad<ImageBitmap>>(
        initialValue = if (fileName == null) PhotoLoad.Unavailable else PhotoLoad.Fetching,
        key1 = ownerUid,
        key2 = fileName,
        key3 = attempt,
    ) {
        if (fileName == null) return@produceState
        value = try {
            val bytes = cache.get(ownerUid, fileName)
                ?: blobs.downloadShared(ownerUid, fileName)?.also { cache.put(ownerUid, fileName, it) }
            bytes?.let(::decodeImageBitmap)?.let { PhotoLoad.Loaded(it) } ?: PhotoLoad.Unavailable
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            PhotoLoad.Unavailable
        }
    }
    return remember(photo, ownerUid, fileName) { StoredPhotoHandle(photo) { attempt++ } }
}
